// Minimal fetch wrapper.
//
// - Sends cookies (same-origin) so the FastAPI session middleware sees the
//   user. Cookies are set by the existing /auth/google/callback handler.
// - On 401 redirects to /auth/google/login (the SPA is mounted under
//   /reports, so the post-login redirect lands the user back here).
// - Other HTTP errors are surfaced as ApiError so screens can render
//   readable messages (the E4 access-denied screen reads this).

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  constructor(status: number, detail: string) {
    super(`API ${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
  }
}

interface ApiOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  // When false, do NOT redirect on 401 (used by the branding endpoint
  // which is public — a 401 there would be a backend bug, not an auth
  // problem).
  redirectOn401?: boolean;
}

export async function api<T>(path: string, opts: ApiOpts = {}): Promise<T> {
  const { method = "GET", body, redirectOn401 = true } = opts;
  const init: RequestInit = {
    method,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
  }

  const r = await fetch(path, init);
  if (r.status === 401 && redirectOn401) {
    // Bounce to the existing Google OAuth login; the backend sets a
    // session cookie on success and redirects back to "/" (the FastAPI
    // root). For Reports we want the user back at /reports/, so the
    // post-login flow needs ?next=/reports/. (The backend's auth router
    // already supports ?next= per its own README.)
    window.location.href = "/auth/google/login?next=/reports/";
    // Never resolves: navigation interrupts.
    return new Promise<never>(() => {});
  }
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const j = (await r.json()) as { detail?: string };
      if (typeof j.detail === "string") detail = j.detail;
    } catch {
      // body wasn't JSON — keep statusText
    }
    throw new ApiError(r.status, detail);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}
